import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Input, Select } from '@dreamtree-org/twreact-ui';
import { Store } from 'lucide-react';
import { useMutation, useQuery } from '@/lib/useQuery';
import SellPicker from '../components/SellPicker';
import useSellCatalog from '../components/useSellCatalog';

const EMPTY = {
  business_name: '', legal_name: '', owner_name: '', email: '', phone: '', gstin: '', pan: '', area: '', city: 'Bengaluru', store_slug: '', plan_key: 'starter', kind: 'store',
  pins: '', sells: { industries: [], types: [] }, expected_units: '50', bank: { bank: '', account_number: '', ifsc: '', holder: '' }, agreement: false,
};

// Seller kinds (D57); the server maps each to capabilities at approval (lib/platform/capabilities.js).
const SELLER_KINDS = [
  { value: 'store', label: 'My own store' },
  { value: 'vendor', label: 'Supplier: other stores resell my products' },
  { value: 'dropshipper', label: 'Reseller: I sell suppliers\' products' },
  { value: 'hybrid', label: 'Both supplier and reseller' },
];

/**
 * "Sell on MerchantPro" (D17): a public application on the platform host. On
 * success the applicant gets a private status link to upload KYC documents.
 */
export default function Apply() {
  const nav = useNavigate();
  const plans = useQuery('platformPublicPlans', {}, { select: (r) => r.data });
  const sellCatalog = useSellCatalog();
  const apply = useMutation('sellerApply');
  const [f, setF] = useState(EMPTY);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setBank = (k, v) => setF((x) => ({ ...x, bank: { ...x.bank, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { sells, ...rest } = f;
      const r = await apply.run({
        ...rest, gstin: f.gstin.trim().toUpperCase(), pan: f.pan.trim().toUpperCase(), store_slug: f.store_slug.trim().toLowerCase(),
        service_regions: f.pins.split(/[\s,]+/).filter(Boolean), industries: sells.industries, product_types: sells.types,
      });
      const token = new URL(r.data.status_url).searchParams.get('token');
      nav(`/apply/status?token=${encodeURIComponent(token)}&new=1`);
    } catch {
      /* shown below */
    }
  };

  return (
    <div className="min-h-dvh bg-[var(--app-bg)] p-4 pt-safe pb-safe">
      <Card className="mx-auto max-w-3xl space-y-5 p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-950"><Store size={24} aria-hidden /></div>
          <div>
            <h1 className="text-xl font-bold">Sell on MerchantPro</h1>
            <p className="text-sm opacity-70">Apply for a store. After this form you'll upload your GST certificate, PAN, bank proof and shop address proof.</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="mb-1 text-sm font-semibold">Business</legend>
            <Input label="Store name" value={f.business_name} onChange={(e) => set('business_name', e.target.value)} required />
            <Input label="Legal name (as on GST)" value={f.legal_name} onChange={(e) => set('legal_name', e.target.value)} required />
            <Input label="GSTIN" value={f.gstin} onChange={(e) => set('gstin', e.target.value)} maxLength={15} required />
            <Input label="PAN" value={f.pan} onChange={(e) => set('pan', e.target.value)} maxLength={10} helperText="Must match the PAN inside the GSTIN. We store it masked." required />
            <Input label="Area" value={f.area} onChange={(e) => set('area', e.target.value)} />
            <Input label="City" value={f.city} onChange={(e) => set('city', e.target.value)} />
            <Input label="PIN codes you serve" value={f.pins} onChange={(e) => set('pins', e.target.value)} placeholder="560038, 560034" />
            <Input label="Items you expect to list" type="number" value={f.expected_units} onChange={(e) => set('expected_units', e.target.value)} />
          </fieldset>
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-semibold">What do you sell?</legend>
            <p className="text-sm opacity-70">Pick your industries and what you sell in each. Your store starts with these product types and the storefront that fits them; you can add more types in the same industries later.</p>
            <SellPicker catalog={sellCatalog} value={f.sells} onChange={(v) => set('sells', v)} withTypes />
          </fieldset>
          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="mb-1 text-sm font-semibold">Owner</legend>
            <Input label="Your name" value={f.owner_name} onChange={(e) => set('owner_name', e.target.value)} required />
            <Input label="Email" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} helperText="Your store's first admin account." required />
            <Input label="Mobile" type="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} required />
          </fieldset>
          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="mb-1 text-sm font-semibold">Store and plan</legend>
            <Input label="Store web name" value={f.store_slug} onChange={(e) => set('store_slug', e.target.value)} helperText="Lowercase letters, digits and dashes; your store address is built from it." required />
            <Select label="Plan" options={(plans.data || []).map((p) => ({ value: p.key, label: `${p.name} · ₹${(p.price_paise / 100).toLocaleString('en-IN')}/month` }))} value={f.plan_key} onChange={(v) => set('plan_key', v || 'starter')} closeOnSelect />
            <Select label="How you sell" options={SELLER_KINDS} value={f.kind} onChange={(v) => set('kind', v || 'store')} closeOnSelect />
          </fieldset>
          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="mb-1 text-sm font-semibold">Settlement bank account</legend>
            <Input label="Bank" value={f.bank.bank} onChange={(e) => setBank('bank', e.target.value)} required />
            <Input label="Account number" inputMode="numeric" value={f.bank.account_number} onChange={(e) => setBank('account_number', e.target.value.replace(/\D/g, ''))} helperText="Only the last four digits are kept." required />
            <Input label="IFSC" value={f.bank.ifsc} onChange={(e) => setBank('ifsc', e.target.value.toUpperCase())} maxLength={11} required />
            <Input label="Account holder" value={f.bank.holder} onChange={(e) => setBank('holder', e.target.value)} helperText="Should match the legal name." />
          </fieldset>
          <Checkbox checked={f.agreement} onChange={(e) => set('agreement', e.target.checked)} label="I accept the MerchantPro seller agreement (SA-2026.3)." />
          {apply.error ? <Alert variant="error">{apply.error}</Alert> : null}
          <Button type="submit" loading={apply.loading} disabled={!f.agreement || !f.sells.types.length}>Continue to documents</Button>
        </form>
      </Card>
    </div>
  );
}
